import { create, StoreApi } from 'zustand';
import { ethers } from 'ethers';

type SnackBar = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

interface StoreState {
  wallet: ethers.JsonRpcSigner | undefined;
  domains: Array<Domain>;
  domainTree: DomainTree | undefined;
  tokens: Array<Token>;
  operators: string[];
  snackbar: SnackBar;
  loadingText: string;
  mode: 'light' | 'dark';
  firstLvlDomains: string[];
  toggleTheme: () => void;
  login: (wallet: ethers.JsonRpcSigner, tree: DomainTree | undefined) => void;
  logout: () => void;
  setDomains: (domains: Domain[]) => void;
  setDomainTree: (tree: DomainTree | undefined) => void;
  clearDomains: () => void;
  setTokens: (tokens: Token[]) => void;
  setOperators: (operators: string[]) => void;
  setSnackbar: (snackBar: SnackBar) => void;
  setLoadingText: (text: string) => void;
  setFirstLvlDomains: (domains: string[]) => void;
}

const useStore = create<StoreState>(set => ({
  wallet: undefined,
  domains: [],
  domainTree: undefined,
  tokens: [],
  operators: [],
  snackbar: {
    open: false,
    message: '',
    severity: 'success',
  },
  loadingText: '',
  mode: 'dark',
  firstLvlDomains: [],
  toggleTheme: () =>
    set(state => ({
      mode: state.mode === 'dark' ? 'light' : 'dark',
    })),
  login: (wallet: ethers.JsonRpcSigner, tree: DomainTree | undefined) =>
    set(() => ({
      wallet: wallet,
      domainTree: tree,
    })),
  logout: () =>
    set(() => ({
      wallet: undefined,
    })),
  setDomains: (domains: Domain[]) =>
    set(() => ({
      domains: domains,
    })),
  setDomainTree: (tree: DomainTree | undefined) =>
    set(() => ({
      domainTree: tree,
    })),
  clearDomains: () =>
    set(() => ({
      domains: [],
    })),
  setTokens: (tokens: Token[]) =>
    set(() => ({
      selectedToken: tokens[0],
      tokens: tokens,
    })),
  setOperators: (operators: string[]) =>
    set(() => ({
      operators: operators,
    })),
  setSnackbar: (snackbar: SnackBar) =>
    set(() => ({
      snackbar: snackbar,
    })),
  setLoadingText: (text: string) =>
    set(() => ({
      loadingText: text,
    })),
  setFirstLvlDomains: (domains: string[]) =>
    set(() => ({
      firstLvlDomains: domains,
    })),
}));

// Extend the global Window interface to include __store__
declare global {
  interface Window {
    __store__?: StoreApi<StoreState>;
  }
}

// Expose the Zustand store to Cypress for testing
if (typeof window !== 'undefined' && window.Cypress) {
  window.__store__ = useStore;
}

export const { getState, setState, subscribe } = useStore;

export default useStore;
